<template>
  <interview-layout :touch-and-validate="touchAndValidate">
    <div>
      <h1 class="px-4 fade-in-up-enter-active animation-slowest max-100 text-primary">
        Extra subsidies.
      </h1>
      <base-form @submit="onSubmit">
        <div class="d-flex flex-column flex-lg-row justify-content-center">
          <base-card inner-class="container-narrow vw-100 w-100" color="alternate" :outer-padding="true">
            <base-input
              v-if="showVeendamEnergyLabelQuestion"
              type="radiobuttons"
              :options="veendamEnergyLabelOptions"
              direction="column"
              name="veendamEnergyLabelCategory"
              v-model:value="veendamEnergyLabelCategory"
            >
              Welk energielabel heeft uw woning?
            </base-input>

            <base-input
              v-if="showEnergyLabelSplitQuestion"
              type="radiobuttons"
              :options="yesNoUnknownOptions"
              name="energyLabelDOrLower"
              v-model:value="lead.otherSubsidies.energyLabelDOrLower"
            >
              Heeft uw woning energielabel D, E, F of G?
            </base-input>

            <base-input
              v-if="showTwoPoorPartsQuestion"
              class="mt-3"
              type="radiobuttons"
              :options="yesNoUnknownOptions"
              name="hasTwoPoorInsulatedParts"
              v-model:value="lead.otherSubsidies.hasTwoPoorInsulatedParts"
            >
              Heeft uw woning minimaal twee slecht geisoleerde bouwdelen?
            </base-input>

            <base-input
              v-if="showVhfBQuestion"
              class="mt-3"
              type="radiobuttons"
              :options="yesNoUnknownOptions"
              name="vhfEnergyLabelBOrLower"
              v-model:value="lead.otherSubsidies.vhfEnergyLabelBOrLower"
            >
              Heeft uw woning energielabel B of C?
            </base-input>

            <base-input
              v-if="showVhfIsolationQuestion"
              class="mt-3"
              type="radiobuttons"
              :options="yesNoUnknownOptions"
              name="vhfNeedsIsolationStandardMeasures"
              v-model:value="lead.otherSubsidies.vhfNeedsIsolationStandardMeasures"
            >
              Zijn er maatregelen nodig om de isolatiestandaard te halen of voldoende labelsprongen te maken?
            </base-input>
          </base-card>
        </div>

        <button-row class="mt-4 flex-row-reverse">
          <base-button
            class="ms-auto"
            icon="bi-arrow-right"
            type="submit"
            :disabled="isIncomplete"
          >
            Volgende
          </base-button>
          <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">
            Terug
          </base-button>
        </button-row>
      </base-form>
    </div>
  </interview-layout>
</template>

<script>
import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseForm from "@/components/BaseForm.vue";
import BaseButton from "@/components/BaseButton.vue";
import BaseInput from "@/components/BaseInput.vue";
import ButtonRow from "@/components/ButtonRow.vue";
import { CommonComposable } from "@/helpers/CommonComposable";
import { useAllStores } from "@/helpers/UseAllStores";
import { FlowService } from "@/services/FlowService";
import { normalizeEnergyLabel, isEnergyLabelAtMost } from "@/helpers/EnergyLabel";

export default {
  components: {
    InterviewLayout,
    BaseCard,
    BaseForm,
    BaseButton,
    BaseInput,
    ButtonRow,
  },
  setup(props, context) {
    return { ...CommonComposable(props, context), ...useAllStores() };
  },
  data() {
    return {
      yesNoUnknownOptions: [
        { label: "Ja", value: true },
        { label: "Nee", value: false },
        { label: "Weet ik niet", value: "unknown" },
      ],
      veendamEnergyLabelOptions: [
        { label: "A of beter", value: "aOrBetter" },
        { label: "B of C", value: "bOrC" },
        { label: "D, E, F of G", value: "dToG" },
        { label: "Weet ik niet", value: "unknown" },
      ],
      yesNoOptions: [
        { label: "Ja", value: true },
        { label: "Nee", value: false },
      ],
    };
  },
  computed: {
    municipality() {
      return this.lead.address?.pc_GemNaam || this.lead.address?.Municipality;
    },
    usesSplitEnergyQuestions() {
      return [
        "Pekela",
        "Midden-Groningen",
        "Veendam",
        "Stadskanaal",
        "Oldambt",
        "Westerwolde",
      ].includes(this.municipality);
    },
    usesVhfIsolationQuestion() {
      return ["Midden-Groningen", "Oldambt"].includes(this.municipality);
    },
    usesVeendamSingleEnergyLabelQuestion() {
      return this.municipality === "Veendam";
    },
    knownEnergyLabel() {
      return normalizeEnergyLabel(this.lead.address?.Energielabel__c);
    },
    hasKnownEnergyLabel() {
      return Boolean(this.knownEnergyLabel);
    },
    knownAtMostD() {
      if (!this.hasKnownEnergyLabel) {
        return null;
      }

      return isEnergyLabelAtMost(this.knownEnergyLabel, "D", { missingAsPass: false });
    },
    showVeendamEnergyLabelQuestion() {
      return this.usesVeendamSingleEnergyLabelQuestion && !this.hasKnownEnergyLabel;
    },
    isSplitSectionComplete() {
      if (this.hasKnownEnergyLabel) {
        if (this.knownAtMostD === true) {
          return true;
        }

        return this.lead.otherSubsidies.hasTwoPoorInsulatedParts !== null;
      }

      if (this.usesVeendamSingleEnergyLabelQuestion) {
        if (this.veendamEnergyLabelCategory === null) {
          return false;
        }

        if (this.veendamEnergyLabelCategory === "dToG") {
          return true;
        }

        return this.lead.otherSubsidies.hasTwoPoorInsulatedParts !== null;
      }

      if (!this.usesSplitEnergyQuestions) {
        return true;
      }

      const firstAnswer = this.lead.otherSubsidies.energyLabelDOrLower;
      if (firstAnswer === null) return false;
      if (firstAnswer === true) return true;
      return this.lead.otherSubsidies.hasTwoPoorInsulatedParts !== null;
    },
    showEnergyLabelSplitQuestion() {
      return (
        this.usesSplitEnergyQuestions &&
        !this.usesVeendamSingleEnergyLabelQuestion &&
        !this.hasKnownEnergyLabel
      );
    },
    showTwoPoorPartsQuestion() {
      if (this.hasKnownEnergyLabel) {
        return this.usesSplitEnergyQuestions && this.knownAtMostD === false;
      }

      if (this.usesVeendamSingleEnergyLabelQuestion) {
        return (
          this.veendamEnergyLabelCategory !== null &&
          this.veendamEnergyLabelCategory !== "dToG"
        );
      }

      return (
        this.usesSplitEnergyQuestions &&
        !this.usesVeendamSingleEnergyLabelQuestion &&
        (this.lead.otherSubsidies.energyLabelDOrLower === false ||
          this.lead.otherSubsidies.energyLabelDOrLower === "unknown")
      );
    },
    showVhfIsolationQuestion() {
      return this.usesVhfIsolationQuestion && this.isSplitSectionComplete;
    },
    showVhfBQuestion() {
      return false;
    },
    veendamEnergyLabelCategory: {
      get() {
        if (!this.usesVeendamSingleEnergyLabelQuestion) {
          return null;
        }

        const first = this.lead.otherSubsidies.energyLabelDOrLower;
        //const second = this.lead.otherSubsidies.hasTwoPoorInsulatedParts;
        const vhfB = this.lead.otherSubsidies.vhfEnergyLabelBOrLower;

        if (first === true) {
          return "dToG";
        }

        // Alleen als energielabelvraag zelf op 'unknown' staat, return 'unknown'
        if (first === "unknown") {
          return "unknown";
        }
        // De bouwdelen-vraag mag niet het energielabel op 'unknown' zetten
        if (vhfB === "unknown") {
          return "unknown";
        }

        if (first === false && vhfB === true) {
          return "bOrC";
        }

        if (first === false && vhfB === false) {
          return "aOrBetter";
        }

        return null;
      },
      set(value) {
        if (!this.usesVeendamSingleEnergyLabelQuestion) {
          return;
        }

        if (value === "dToG") {
          this.lead.otherSubsidies.energyLabelDOrLower = true;
          this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
          this.lead.otherSubsidies.vhfEnergyLabelBOrLower = null;
          this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
          return;
        }

        if (value === "bOrC") {
          this.lead.otherSubsidies.energyLabelDOrLower = false;
          this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
          this.lead.otherSubsidies.vhfEnergyLabelBOrLower = true;
          this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
          return;
        }

        if (value === "aOrBetter") {
          this.lead.otherSubsidies.energyLabelDOrLower = false;
          this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
          this.lead.otherSubsidies.vhfEnergyLabelBOrLower = false;
          this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
          return;
        }

        if (value === "unknown") {
          this.lead.otherSubsidies.energyLabelDOrLower = "unknown";
          this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
          this.lead.otherSubsidies.vhfEnergyLabelBOrLower = "unknown";
          this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
          return;
        }

        this.lead.otherSubsidies.energyLabelDOrLower = null;
        this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
        this.lead.otherSubsidies.vhfEnergyLabelBOrLower = null;
        this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
      },
    },
    hasAnyQuestion() {
      return (
        this.showVeendamEnergyLabelQuestion ||
        this.showEnergyLabelSplitQuestion ||
        this.showTwoPoorPartsQuestion ||
        this.showVhfBQuestion ||
        this.showVhfIsolationQuestion
      );
    },
    isIncomplete() {
      if (this.usesSplitEnergyQuestions && !this.isSplitSectionComplete) {
        return true;
      }

      if (
        this.showVhfBQuestion &&
        this.lead.otherSubsidies.vhfEnergyLabelBOrLower === null
      ) {
        return true;
      }

      if (
        this.showVhfIsolationQuestion &&
        this.lead.otherSubsidies.vhfNeedsIsolationStandardMeasures === null
      ) {
        return true;
      }

      return false;
    },
  },
  watch: {
    "lead.otherSubsidies.energyLabelDOrLower"() {
      if (!this.showTwoPoorPartsQuestion) {
        this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
      }
      this.syncSplitAnswersToCombined();
    },
    "lead.otherSubsidies.hasTwoPoorInsulatedParts"() {
      this.syncSplitAnswersToCombined();
    },
    "lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts"() {
      if (!this.showVhfBQuestion && !this.usesVeendamSingleEnergyLabelQuestion) {
        this.lead.otherSubsidies.vhfEnergyLabelBOrLower = null;
      }
    },
    municipality() {
      this.normalizeByMunicipality();
    },
    "lead.address.Energielabel__c"() {
      this.normalizeByMunicipality();
      this.syncSplitAnswersToCombined();
    },
  },
  methods: {
    syncSplitAnswersToCombined() {
      if (!this.usesSplitEnergyQuestions) {
        return;
      }

      const secondAnswer = this.lead.otherSubsidies.hasTwoPoorInsulatedParts;

      if (this.hasKnownEnergyLabel) {
        if (this.knownAtMostD === true) {
          this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = true;
          return;
        }

        if (this.knownAtMostD === false) {
          if (secondAnswer === null) {
            this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
            return;
          }

          this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts =
            secondAnswer === true || secondAnswer === "unknown";
          return;
        }
      }

      const firstAnswer = this.lead.otherSubsidies.energyLabelDOrLower;

      if (firstAnswer === null) {
        this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
        return;
      }

      if (firstAnswer === true) {
        this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = true;
        return;
      }

      if (secondAnswer === null) {
        this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts = null;
        return;
      }

      this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts =
        secondAnswer === true || secondAnswer === "unknown";
    },
    normalizeByMunicipality() {
      if (!this.usesSplitEnergyQuestions) {
        this.lead.otherSubsidies.energyLabelDOrLower = null;
        this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
        this.lead.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts =
          null;
      }

      if (this.usesSplitEnergyQuestions && this.hasKnownEnergyLabel) {
        this.lead.otherSubsidies.energyLabelDOrLower = this.knownAtMostD === true;

        if (this.knownAtMostD === true) {
          this.lead.otherSubsidies.hasTwoPoorInsulatedParts = null;
        }
      }

      if (!this.usesVhfIsolationQuestion) {
        this.lead.otherSubsidies.vhfNeedsIsolationStandardMeasures = null;
      }

      if (this.municipality !== "Veendam") {
        this.lead.otherSubsidies.vhfEnergyLabelBOrLower = null;
      }
    },
    touchAndValidate() {
      this.v$.otherSubsidies.energyLabelDOrLower.$touch();
      this.v$.otherSubsidies.hasTwoPoorInsulatedParts.$touch();
      this.v$.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts.$touch();
      this.v$.otherSubsidies.vhfEnergyLabelBOrLower.$touch();
      this.v$.otherSubsidies.vhfNeedsIsolationStandardMeasures.$touch();

      return !this.isIncomplete;
    },
    onSubmit() {
      if (this.touchAndValidate()) {
        FlowService.pushNext(this);
      }
    },
    onPrev() {
      FlowService.pushPrev(this);
    },
  },
  mounted() {
    this.normalizeByMunicipality();
    this.syncSplitAnswersToCombined();
  },
};
</script>

<style scoped lang="scss">

</style>
